import moment from "moment"
import queryString from "query-string"
import { InboundTransportOrderFetchParams } from "@/client/transportorder-hub/transportOrderHubApi"
import { TransportOrderType } from "@/model/transportorder/transportOrder"

export enum InboundDateFilterType {
    CREATED_AT = "created_at",
    PICKUP_REQUESTED_AT = "pickup_requested_at",
    ARRIVAL_ESTIMATED_AT = "arrival_estimated_at",
    DELIVERY_ESTIMATED_AT = "delivery_estimated_at",
}

export enum InboundSortField {
    DOCUMENT_DATE = "documentDate",
    REQUESTED_PICKUP_DATE_AT_SHIP_FROM = "requestedPickupDateAtShipFrom",
    ESTIMATED_ARRIVAL_DATE_OF_CURRENT_LEG = "estimatedArrivalDateOfCurrentLeg",
    CALCULATED_ARRIVAL_DATE_AT_SHIP_TO = "calculatedArrivalDateAtShipTo",
}

export type InboundSortDirection = "asc" | "desc"

export interface InboundTransportOrderUrlState {
    dateFrom: Date
    dateTo: Date
    dateFilterType: InboundDateFilterType
    orderType: TransportOrderType | undefined
    searchPhrase: string
    sortBy: InboundSortField
    sortDirection: InboundSortDirection
}

const readString = (value: string | (string | null)[] | null | undefined): string | undefined =>
    typeof value === "string" ? value : undefined

const parseLocalDate = (value: string | undefined, fallback: Date): Date => {
    if (!value) {
        return fallback
    }
    const parsed = new Date(value)
    return isFinite(parsed.getTime()) ? parsed : fallback
}

export const createDefaultInboundUrlState = (today: Date = new Date()): InboundTransportOrderUrlState => ({
    dateFrom: moment(today).subtract(14, "days").startOf("day").toDate(),
    dateTo: moment(today).add(14, "days").endOf("day").toDate(),
    dateFilterType: InboundDateFilterType.CREATED_AT,
    orderType: undefined,
    searchPhrase: "",
    sortBy: InboundSortField.CALCULATED_ARRIVAL_DATE_AT_SHIP_TO,
    sortDirection: "asc",
})

export const extractInboundUrlState = (
    search: string,
    today: Date = new Date(),
): InboundTransportOrderUrlState => {
    const parsed = queryString.parse(search)
    const defaults = createDefaultInboundUrlState(today)
    const dateFilterTypeValue = readString(parsed.dateFilterType)
    const orderTypeValue = readString(parsed.orderType)
    const sortByValue = readString(parsed.sortBy)
    const sortDirectionValue = readString(parsed.sortDir)

    return {
        dateFrom: parseLocalDate(readString(parsed.dateFrom), defaults.dateFrom),
        dateTo: parseLocalDate(readString(parsed.dateTo), defaults.dateTo),
        dateFilterType: Object.values(InboundDateFilterType).includes(dateFilterTypeValue as InboundDateFilterType)
            ? dateFilterTypeValue as InboundDateFilterType
            : defaults.dateFilterType,
        orderType: Object.values(TransportOrderType).includes(orderTypeValue as TransportOrderType)
            ? orderTypeValue as TransportOrderType
            : undefined,
        searchPhrase: readString(parsed.searchPhrase) ?? defaults.searchPhrase,
        sortBy: Object.values(InboundSortField).includes(sortByValue as InboundSortField)
            ? sortByValue as InboundSortField
            : defaults.sortBy,
        sortDirection: sortDirectionValue === "desc" ? "desc" : defaults.sortDirection,
    }
}

export const stringifyInboundUrlState = (state: InboundTransportOrderUrlState): string => queryString.stringify({
    dateFilterType: state.dateFilterType,
    dateFrom: state.dateFrom.toISOString(),
    dateTo: state.dateTo.toISOString(),
    orderType: state.orderType,
    searchPhrase: state.searchPhrase,
    sortBy: state.sortBy,
    sortDir: state.sortDirection,
})

export const mapInboundDateRangeToFetchParams = (
    state: Pick<InboundTransportOrderUrlState, "dateFilterType" | "dateFrom" | "dateTo">,
): InboundTransportOrderFetchParams => {
    const from = state.dateFrom.toISOString()
    const to = state.dateTo.toISOString()
    switch (state.dateFilterType) {
    case InboundDateFilterType.PICKUP_REQUESTED_AT:
        return { fromPickupRequestedAt: from, toPickupRequestedAt: to }
    case InboundDateFilterType.ARRIVAL_ESTIMATED_AT:
        return { fromArrivalEstimatedAt: from, toArrivalEstimatedAt: to }
    case InboundDateFilterType.DELIVERY_ESTIMATED_AT:
        return { fromDeliveryEstimatedAt: from, toDeliveryEstimatedAt: to }
    default:
        return { fromCreatedAt: from, toCreatedAt: to }
    }
}
